import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Plus, Edit, Trash2, FileText } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import DeleteModal from "../../components/admin/DeleteModal";
import { useNavigate } from "react-router-dom";
import SearchBar from "../ui/search-bar";
import {
  fetchCertifications,
  removeCertification,
} from "@/hooks/useCertification";
import { isPdfFile } from "@/utils/certificateFile";

const CertificationsManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    certification: null,
  });
  const [certificationList, setCertificationList] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  const getAllCertifications = async () => {
    try {
      const res = await fetchCertifications();
      setCertificationList(res.certifications || res);
    } catch (err) {
      toast({
        title: "Error",
        description:
          err.response?.data?.message || "Failed to load certifications.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    getAllCertifications();
  }, []);

  const filteredCertifications = certificationList.filter(
    (cert) =>
      cert.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    try {
      await removeCertification(id);
      toast({
        title: "Deleted",
        description: "Certification removed successfully.",
      });
      setDeleteModal({ isOpen: false, certification: null });
      getAllCertifications();
    } catch (err) {
      toast({
        title: "Error",
        description:
          err.response?.data?.message || "Failed to delete certification.",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Certifications Management</h2>
          <p className="text-muted-foreground">
            Manage your professional certifications and credentials
          </p>
        </div>
        <Button
          className="bg-purple-600 hover:bg-purple-700"
          onClick={() => navigate("/admin/certifications/add")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Certification
        </Button>
      </div>

      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      <div className="space-y-4">
        <AnimatePresence>
          {filteredCertifications.map((cert, index) => (
            <motion.div
              key={cert._id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              whileHover={{ scale: 1.01 }}
            >
              <Card className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    {isPdfFile(cert.file) ? (
                      <div className="w-20 h-20 rounded-lg border bg-muted flex items-center justify-center shrink-0">
                        <FileText className="w-8 h-8 text-red-500" />
                      </div>
                    ) : (
                      <img
                        src={cert.file?.url}
                        alt={cert.title}
                        className="w-20 h-20 rounded-lg object-cover shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2 min-w-0">
                          <h3 className="text-lg font-semibold hover:text-purple-600 transition-colors truncate">
                            {cert.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {cert.industry}
                            </Badge>
                            {cert.company && (
                              <Badge variant="secondary" className="text-xs">
                                {cert.company}
                              </Badge>
                            )}
                            <span>
                              {cert.issueDate
                                ? new Date(cert.issueDate).toLocaleDateString()
                                : "—"}
                            </span>
                            {cert.verifyUrl && (
                              <Badge variant="secondary" className="text-xs">
                                Has verify link
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-blue-50 dark:hover:bg-blue-950/20"
                            onClick={() =>
                              navigate(
                                `/admin/certifications/edit/${cert._id}`
                              )
                            }
                          >
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-red-50 dark:hover:bg-red-950/20"
                            onClick={() =>
                              setDeleteModal({
                                isOpen: true,
                                certification: cert,
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>

        {filteredCertifications.length === 0 && (
          <p className="text-center text-muted-foreground py-12">
            No certifications found.
          </p>
        )}
      </div>

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, certification: null })
        }
        onConfirm={() => handleDelete(deleteModal.certification?._id)}
        title="Delete Certification"
        description="This will permanently remove the certification from your portfolio."
        itemName={deleteModal.certification?.title}
      />
    </motion.div>
  );
};

export default CertificationsManagement;
